import { TransactionReceipt, TransactionRequest } from 'ethers'
import { ReactNode, useCallback, useRef } from 'react'
import { createContext, useContext, useState } from 'react'

import { Token } from '@/store/tokens'

import TransactionConfirmationModal from './components/TransactionConfirmationModal'

export type TransactionStatus = 'pending' | 'processing' | 'success' | 'failed'

export type SubmittedTransactionResponse = 'cancel' | Error | TransactionReceipt

export type FnTxReceiptAlias = () => Promise<TransactionReceipt>

export type TransactionRequestStep = {
  title: string
  description?: string
  metadata?: Record<string, string | ReactNode>
  txRequest: TransactionRequest | FnTxReceiptAlias

  fromToken?: Token & {
    amount: string
  }
  toToken?: Token & {
    amount: string
  }
}

export interface TransactionConfirmationRequest {
  steps: TransactionRequestStep[]
}

export interface TransactionContextType {
  submitTransactions: (
    txDetails: TransactionConfirmationRequest,
  ) => Promise<SubmittedTransactionResponse[]>
}

const TransactionContext = createContext<TransactionContextType>({
  submitTransactions: async () => {
    throw new TypeError('Not implemented')
  },
})

export const useTransactions = () => useContext(TransactionContext)

export const TransactionProvider = ({ children }: { children: ReactNode }) => {
  // Transaction state
  const [showTransactionModal, setShowTransactionModal] = useState(false)

  const submitTransactionResolve =
    useRef<(value: SubmittedTransactionResponse[]) => void>()

  const [transactionDetails, setTransactionDetails] =
    useState<TransactionConfirmationRequest>()

  const submitTransactions = useCallback(
    async (txDetails: TransactionConfirmationRequest) => {
      return await new Promise<SubmittedTransactionResponse[]>(resolve => {
        submitTransactionResolve.current = resolve
        setTransactionDetails(txDetails)
        setShowTransactionModal(true)
      })
    },
    [],
  )

  return (
    <TransactionContext.Provider value={{ submitTransactions }}>
      {children}

      {/* Transaction Confirmation Modal */}
      {transactionDetails && (
        <TransactionConfirmationModal
          isOpen={showTransactionModal}
          txDetails={transactionDetails}
          handleCancel={() => {
            setShowTransactionModal(false)
            setTransactionDetails(undefined)
            submitTransactionResolve.current?.(['cancel'])
          }}
          handleDismiss={errors => {
            setShowTransactionModal(false)
            setTransactionDetails(undefined)
            submitTransactionResolve.current?.(errors || [])
          }}
          handleSuccess={txReceipt => {
            setShowTransactionModal(false)
            setTransactionDetails(undefined)
            submitTransactionResolve.current?.(txReceipt)
          }}
        />
      )}
    </TransactionContext.Provider>
  )
}
